import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { trainingApi, pdvApi } from '../lib/api';
import { toast } from 'sonner';
import {
  GraduationCap,
  Plus,
  Search,
  Filter,
  Calendar,
  Building,
  User,
  Pencil,
  Trash2,
  AlertTriangle,
  Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const COURSE_TYPES = [
  { value: 'sicurezza_generale', label: 'Sicurezza Generale' },
  { value: 'sicurezza_specifica', label: 'Sicurezza Specifica' },
  { value: 'antincendio', label: 'Antincendio' },
  { value: 'primo_soccorso', label: 'Primo Soccorso' },
  { value: 'haccp', label: 'HACCP' },
  { value: 'rls', label: 'RLS' },
  { value: 'preposto', label: 'Preposto' },
  { value: 'dirigente', label: 'Dirigente' },
  { value: 'altro', label: 'Altro' }
];

const StatusBadge = ({ status }) => {
  const variants = {
    valid: 'bg-emerald-100 text-emerald-800',
    expiring: 'bg-amber-100 text-amber-800',
    expired: 'bg-rose-100 text-rose-800'
  };

  const labels = {
    valid: 'Valido',
    expiring: 'In Scadenza',
    expired: 'Scaduto'
  };

  return (
    <Badge className={variants[status] || variants.valid}>
      {labels[status] || status}
    </Badge>
  );
};

const TrainingForm = ({ training, pdvs, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(training || {
    pdv_id: '',
    employee_name: '',
    course_name: '',
    course_type: '',
    completion_date: '',
    expiry_date: '',
    certificate_url: '',
    notes: ''
  });
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/upload/certificates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      const data = await response.json();
      setFormData({ ...formData, certificate_url: data.url });
      toast.success('Attestato caricato con successo');
    } catch (error) {
      toast.error('Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pdv_id">Unità Locale *</Label>
        <Select
          value={formData.pdv_id}
          onValueChange={(value) => setFormData({ ...formData, pdv_id: value })}
        >
          <SelectTrigger data-testid="training-pdv-select">
            <SelectValue placeholder="Seleziona UL" />
          </SelectTrigger>
          <SelectContent>
            {pdvs.map((pdv) => (
              <SelectItem key={pdv.id} value={pdv.id}>
                {pdv.name} - {pdv.city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="employee_name">Nome Dipendente *</Label>
        <Input
          id="employee_name"
          value={formData.employee_name}
          onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
          placeholder="Mario Rossi"
          required
          data-testid="training-employee-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="course_name">Nome Corso *</Label>
        <Input
          id="course_name"
          value={formData.course_name}
          onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
          placeholder="Corso sicurezza lavoratori"
          required
          data-testid="training-course-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="course_type">Tipo Corso *</Label>
        <Select
          value={formData.course_type}
          onValueChange={(value) => setFormData({ ...formData, course_type: value })}
        >
          <SelectTrigger data-testid="training-type-select">
            <SelectValue placeholder="Seleziona tipo" />
          </SelectTrigger>
          <SelectContent>
            {COURSE_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="completion_date">Data Completamento *</Label>
          <Input
            id="completion_date"
            type="date"
            value={formData.completion_date}
            onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
            required
            data-testid="training-completion-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="expiry_date">Data Scadenza</Label>
          <Input
            id="expiry_date"
            type="date"
            value={formData.expiry_date}
            onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
            data-testid="training-expiry-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Carica Attestato</Label>
        <div className="flex items-center gap-2">
          <Input
            type="file"
            onChange={handleFileUpload}
            disabled={uploading}
            className="flex-1"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            data-testid="training-file-input"
          />
          {uploading && <span className="text-sm text-muted-foreground">Caricamento...</span>}
        </div>
        {formData.certificate_url && (
          <p className="text-xs text-emerald-600 flex items-center gap-1">
            <Upload className="w-3 h-3" /> Attestato caricato
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="certificate_url">Oppure inserisci URL</Label>
        <Input
          id="certificate_url"
          value={formData.certificate_url}
          onChange={(e) => setFormData({ ...formData, certificate_url: e.target.value })}
          placeholder="https://..."
          data-testid="training-url-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Note</Label>
        <textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Note aggiuntive..."
          className="w-full min-h-[60px] px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
          data-testid="training-notes-input"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" data-testid="training-submit-btn">
          {training ? 'Salva Modifiche' : 'Aggiungi Formazione'}
        </Button>
      </div>
    </form>
  );
};

export default function TrainingPage() {
  const [trainings, setTrainings] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPdv, setFilterPdv] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trainingToDelete, setTrainingToDelete] = useState(null);

  const fetchData = async () => {
    try {
      const [trainingsRes, pdvsRes] = await Promise.all([
        trainingApi.getAll(),
        pdvApi.getAll()
      ]);
      setTrainings(trainingsRes.data);
      setPdvs(pdvsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (data) => {
    try {
      if (editingTraining) {
        await trainingApi.update(editingTraining.id, data);
        toast.success('Formazione aggiornata');
      } else {
        await trainingApi.create(data);
        toast.success('Formazione aggiunta');
      }
      setDialogOpen(false);
      setEditingTraining(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleEdit = (training) => {
    setEditingTraining(training);
    setDialogOpen(true);
  };

  const handleDeleteClick = (training) => {
    setTrainingToDelete(training);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!trainingToDelete) return;
    try {
      await trainingApi.delete(trainingToDelete.id);
      toast.success('Formazione eliminata');
      setDeleteDialogOpen(false);
      setTrainingToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const getPdvName = (pdvId) => {
    const pdv = pdvs.find(p => p.id === pdvId);
    return pdv ? pdv.name : 'N/D';
  };

  const getTypeLabel = (type) => {
    const courseType = COURSE_TYPES.find(t => t.value === type);
    return courseType ? courseType.label : type;
  };

  const filteredTrainings = trainings.filter((t) => {
    const matchesSearch = 
      t.employee_name.toLowerCase().includes(search.toLowerCase()) ||
      t.course_name.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'all' || t.course_type === filterType;
    const matchesPdv = filterPdv === 'all' || t.pdv_id === filterPdv;
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    return matchesSearch && matchesType && matchesPdv && matchesStatus;
  });

  const expiredCount = trainings.filter(t => t.status === 'expired').length;
  const expiringCount = trainings.filter(t => t.status === 'expiring').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="training-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="page-header mb-0">
          <h1 className="page-title">Formazione</h1>
          <p className="page-subtitle">{trainings.length} record formativi</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingTraining(null);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-training-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Formazione
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingTraining ? 'Modifica Formazione' : 'Nuova Formazione'}
              </DialogTitle>
            </DialogHeader>
            <TrainingForm
              training={editingTraining}
              pdvs={pdvs}
              onSubmit={handleSubmit}
              onCancel={() => {
                setDialogOpen(false);
                setEditingTraining(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Alert Banner */}
      {(expiredCount > 0 || expiringCount > 0) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert-banner warning mb-6 flex items-center gap-3"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">
            <strong>{expiredCount} attestati scaduti</strong> e <strong>{expiringCount} in scadenza</strong> nei prossimi 30 giorni
          </p>
        </motion.div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per dipendente o corso..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="training-search-input"
          />
        </div>
        <Select value={filterPdv} onValueChange={setFilterPdv}>
          <SelectTrigger data-testid="filter-training-pdv">
            <Building className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Tutte le UL" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le UL</SelectItem>
            {pdvs.map((pdv) => (
              <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="filter-training-type">
            <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Tipo Corso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {COURSE_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="filter-training-status">
            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="valid">Validi</SelectItem>
            <SelectItem value="expiring">In Scadenza</SelectItem>
            <SelectItem value="expired">Scaduti</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {filteredTrainings.length > 0 ? (
        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="table-header">Dipendente</TableHead>
                <TableHead className="table-header">Corso</TableHead>
                <TableHead className="table-header">UL</TableHead>
                <TableHead className="table-header">Tipo</TableHead>
                <TableHead className="table-header">Completato</TableHead>
                <TableHead className="table-header">Scadenza</TableHead>
                <TableHead className="table-header">Stato</TableHead>
                <TableHead className="table-header w-[80px]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrainings.map((t) => (
                <TableRow key={t.id} className="data-row" data-testid={`training-row-${t.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{t.employee_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{t.course_name}</TableCell>
                  <TableCell>{getPdvName(t.pdv_id)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{getTypeLabel(t.course_type)}</Badge>
                  </TableCell>
                  <TableCell>{new Date(t.completion_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</TableCell>
                  <TableCell>
                    {t.expiry_date 
                      ? new Date(t.expiry_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={t.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleEdit(t)}
                        data-testid={`edit-training-${t.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClick(t)}
                        data-testid={`delete-training-${t.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="empty-state">
          <GraduationCap className="empty-state-icon" />
          <p className="empty-state-title">
            {search || filterType !== 'all' || filterPdv !== 'all' || filterStatus !== 'all'
              ? 'Nessun risultato'
              : 'Nessuna formazione'
            }
          </p>
          <p className="empty-state-description">
            {search || filterType !== 'all' || filterPdv !== 'all' || filterStatus !== 'all'
              ? 'Prova a modificare i filtri'
              : 'Inizia aggiungendo il primo record formativo'
            }
          </p>
          {!search && filterType === 'all' && filterPdv === 'all' && filterStatus === 'all' && (
            <Button className="mt-4" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi formazione
            </Button>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare la formazione di "{trainingToDelete?.employee_name}"? 
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-training"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
